import React, { type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import GuestLayout from '../../domains/guest-landing/guest-layout';
import DashboardHome from '../../domains/dashboard/pages/home';
import UserSettingsPage from '../../domains/user/ui/pages/UserSettingsPage';
import EducationProfilePage from '../../domains/user/ui/pages/EducationProfilePage';
import {
  PublicMentorProfilePage,
  PublicStudentProfilePage,
} from '../../domains/public';
import WebSite from '../../domains/dashboard/components/web-site';
import Blog from '../../domains/dashboard/pages/blog';
import Support from '../../domains/dashboard/pages/support';
import { SignIn, SignUp, ForgotPassword } from '../../domains/auth';
import Validation from '../../domains/landing/ui/pages/Validation';
import GuestHome from '../../domains/guest-landing/pages/home';
import AboutUsPage from '../../domains/about-us';
import MeetPage from '../../domains/meet';
import ChangelogPage from '../../domains/changelog';
import PublicOfferPage from '../../domains/legal';
import WalletHome from '../../domains/wallet/ui/pages/Home';
import Funding from '../../domains/wallet/ui/pages/Funding';
import Withdraw from '../../domains/wallet/ui/pages/Withdraw';
import WalletLayout from '../../domains/wallet/ui/components/WalletLayout';
import { ChatHomePage, ChatLayout } from '../../domains/chat';
import {
  NotificationHomePage,
  NotificationLayout,
} from '../../domains/notification';
import MentorListPage from '../../domains/mentor/ui/pages/MentorListPage';
import MentorBookPage from '../../domains/mentor/ui/pages/MentorBookPage';
import ConversationPage from '../../domains/conversation/ui/pages/ConversationPage';
import { SearchPage } from '../../domains/search';
import {
  LessonCreatePage,
  LessonEditPage,
  LessonListPage,
} from '../../domains/lesson';
import WebBuilderLayout from '../../domains/dashboard/modules/web-builder/layout';
import WebBuilderHome from '../../domains/dashboard/modules/web-builder/components/home';
import { defaultRoutesByRole, paths, rolePath, roles, type AppRole } from './paths';
import { useAppRole } from './useRolePath';
import type { AppRoute, RoutesByRoleMap } from './types';

function wrap(Layout: React.ComponentType<{ children?: ReactNode }>, page: ReactNode) {
  return <Layout>{page}</Layout>;
}

function ReadSlugRedirect() {
  const { slug = '' } = useParams();
  const role = useAppRole();
  return <Navigate to={`/${role}/read/${slug}`} replace />;
}

export function PublicMentorRedirect() {
  const { slug = '' } = useParams();
  return (
    <Navigate to={`/public/mentor/${encodeURIComponent(slug)}`} replace />
  );
}

const guest = (page: ReactNode) => wrap(GuestLayout, page);
const wallet = (page: ReactNode) => wrap(WalletLayout, page);
const chat = (page: ReactNode) => wrap(ChatLayout, page);
const messages = (page: ReactNode) => wrap(NotificationLayout, page);
const webBuilder = (page: ReactNode) => wrap(WebBuilderLayout, page);

const sharedAuthenticatedPages: Array<{
  path: string;
  element: ReactNode;
  pageTitle?: string;
}> = [
  { path: paths.dashboard, element: <DashboardHome />, pageTitle: 'dashboard' },
  { path: paths.profile, element: <EducationProfilePage />, pageTitle: 'profile' },
  { path: paths.account, element: <UserSettingsPage />, pageTitle: 'profile' },
  { path: paths.webSite, element: <WebSite />, pageTitle: 'web-site' },
  { path: paths.blog, element: <Blog />, pageTitle: 'blog' },
  { path: paths.support, element: <Support />, pageTitle: 'support' },
  { path: paths.wallet, element: wallet(<WalletHome />), pageTitle: 'wallet' },
  { path: paths.walletFunding, element: wallet(<Funding />) },
  { path: paths.walletWithdraw, element: wallet(<Withdraw />) },
  { path: paths.mentors, element: <MentorListPage />, pageTitle: 'mentors' },
  { path: '/mentors/:slug', element: <MentorBookPage />, pageTitle: 'mentors' },
  { path: paths.search, element: <SearchPage />, pageTitle: 'search' },
  { path: paths.conversations, element: <ConversationPage />, pageTitle: 'messages' },
  { path: '/conversations/:id', element: <ConversationPage />, pageTitle: 'messages' },
  { path: paths.chat, element: chat(<ChatHomePage />), pageTitle: 'chat' },
  { path: paths.messages, element: messages(<NotificationHomePage />), pageTitle: 'messages' },
  { path: paths.webBuilder, element: webBuilder(<WebBuilderHome />) },
];

function prefixRole(
  role: AppRole,
  extras: Array<{ path: string; element: ReactNode; pageTitle?: string }> = [],
): AppRoute[] {
  return [...sharedAuthenticatedPages, ...extras].map((item) => ({
    route: rolePath(role, item.path),
    element: item.element,
    pageTitle: item.pageTitle,
  }));
}

export { defaultRoutesByRole, paths, rolePath, roles };

export const routesByRole: RoutesByRoleMap = {
  none: [
    { route: paths.home, element: guest(<GuestHome />), pageTitle: 'home' },
    { route: paths.aboutUs, element: guest(<AboutUsPage />), pageTitle: 'about-us' },
    { route: paths.meet, element: guest(<MeetPage />), pageTitle: 'meet' },
    { route: paths.changelog, element: guest(<ChangelogPage />), pageTitle: 'changelog' },
    { route: paths.publicOffer, element: guest(<PublicOfferPage />), pageTitle: 'public-offer' },
    { route: paths.signIn, element: guest(<SignIn />), pageTitle: 'sign-in' },
    { route: paths.signUp, element: guest(<SignUp />), pageTitle: 'sign-up' },
    { route: paths.forgotPassword, element: guest(<ForgotPassword />), pageTitle: 'forgot-password' },
    { route: paths.validation, element: guest(<Validation />) },
    {
      route: paths.mentors,
      element: guest(<MentorListPage />),
      pageTitle: 'mentors',
    },
    {
      route: paths.search,
      element: guest(<SearchPage />),
      pageTitle: 'search',
    },
    {
      route: '/public/mentor/:slug',
      element: guest(<PublicMentorProfilePage />),
      pageTitle: 'mentor',
    },
    {
      route: '/mentors/:slug',
      element: guest(<PublicMentorRedirect />),
    },
    {
      route: '/students/:slug',
      element: guest(<PublicStudentProfilePage />),
      pageTitle: 'student',
    },
  ],
  student: prefixRole(roles.CLIENT, [
    {
      path: '/student-profile',
      element: (
        <Navigate to={rolePath(roles.CLIENT, paths.profile)} replace />
      ),
    },
    {
      path: '/read/:slug',
      element: <PublicStudentProfilePage />,
      pageTitle: 'student',
    },
    {
      path: '/view/:slug',
      element: <ReadSlugRedirect />,
    },
    {
      path: '/pre-edit',
      element: <EducationProfilePage />,
      pageTitle: 'profile',
    },
    {
      path: '/edit',
      element: (
        <Navigate to={rolePath(roles.CLIENT, paths.profile)} replace />
      ),
    },
  ]),
  mentor: prefixRole(roles.MENTOR, [
    {
      path: '/mentor-profile',
      element: (
        <Navigate to={rolePath(roles.MENTOR, paths.profile)} replace />
      ),
    },
    {
      path: '/read/:slug',
      element: <PublicMentorProfilePage />,
      pageTitle: 'mentor',
    },
    {
      path: '/view/:slug',
      element: <ReadSlugRedirect />,
    },
    {
      path: '/pre-edit',
      element: <EducationProfilePage />,
      pageTitle: 'profile',
    },
    {
      path: '/edit',
      element: (
        <Navigate to={rolePath(roles.MENTOR, paths.profile)} replace />
      ),
    },
    {
      path: paths.lessonCreate,
      element: <LessonCreatePage />,
      pageTitle: 'lessons',
    },
    {
      path: '/lessons/:lessonOfferingId/edit',
      element: <LessonEditPage />,
      pageTitle: 'lessons',
    },
    {
      path: paths.lessons,
      element: <LessonListPage />,
      pageTitle: 'lessons',
    },
  ]),
};
